import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { isOndemandStorefront, storefrontThemeId } from "@/lib/storefrontTheme";

const ROUTE = [
  [46, 132],
  [108, 132],
  [148, 98],
  [206, 98],
  [246, 68],
  [312, 68],
];

export function rememberOndemandOrder(slug, orderId) {
  try { localStorage.setItem(`ms_od_live_${slug}`, orderId); } catch { /* private mode */ }
  try { window.dispatchEvent(new Event("ondemand-live-order")); } catch { /* ignore */ }
}

export function readOndemandOrder(slug) {
  try { return localStorage.getItem(`ms_od_live_${slug}`) || ""; } catch { return ""; }
}

export function forgetOndemandOrder(slug) {
  try { localStorage.removeItem(`ms_od_live_${slug}`); } catch { /* private mode */ }
}

export function trackingProgress(tr, now = Date.now()) {
  if (!tr) return 0;
  if (tr.phase === "delivered") return 1;
  if (tr.phase === "nearby" || tr.phase === "delayed") return 0.88;
  const created = Date.parse(tr.created_at);
  const prepUntil = Date.parse(tr.prep_until || tr.created_at);
  const etaAt = Date.parse(tr.eta_at);
  if (!Number.isFinite(created) || !Number.isFinite(etaAt)) return Number(tr.progress) || 0;
  if (tr.phase === "prep" || (Number.isFinite(prepUntil) && now < prepUntil)) {
    const elapsed = Math.max(0, (now - created) / 1000);
    return Math.min(0.18, (elapsed / 300) * 0.18);
  }
  const span = Math.max(1, (etaAt - prepUntil) / 1000);
  const t = Math.min(1, Math.max(0, (now - prepUntil) / 1000 / span));
  return 0.18 + t * 0.7;
}

function pointAlong(points, t) {
  const lens = [];
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const len = Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
    lens.push(len);
    total += len;
  }
  let dist = Math.min(1, Math.max(0, t)) * total;
  for (let i = 1; i < points.length; i += 1) {
    const len = lens[i - 1] || 1;
    if (dist <= len || i === points.length - 1) {
      const r = Math.min(1, dist / len);
      const x = points[i - 1][0] + (points[i][0] - points[i - 1][0]) * r;
      const y = points[i - 1][1] + (points[i][1] - points[i - 1][1]) * r;
      const ang = Math.atan2(points[i][1] - points[i - 1][1], points[i][0] - points[i - 1][0]) * (180 / Math.PI);
      return { x, y, ang };
    }
    dist -= len;
  }
  return { x: points[0][0], y: points[0][1], ang: 0 };
}

export function useOndemandTracking(slug, orderId, active) {
  const [tr, setTr] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active || !slug || !orderId) return undefined;
    let alive = true;
    const load = () => {
      api.get(`/store/${slug}/orders/${orderId}/tracking`)
        .then(({ data }) => { if (alive) setTr(data); })
        .catch(() => { if (alive) setTr({ enabled: false }); });
    };
    load();
    const poll = setInterval(load, 8000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { alive = false; clearInterval(poll); clearInterval(tick); };
  }, [slug, orderId, active]);
  return { tr, now };
}

function RiderSprite() {
  return (
    <svg viewBox="0 0 64 64" width="54" height="54" aria-hidden="true">
      <ellipse cx="32" cy="58" rx="16" ry="4" fill="rgba(0,0,0,0.18)" />
      <circle cx="22" cy="46" r="7" fill="#1f2937" />
      <circle cx="46" cy="46" r="7" fill="#1f2937" />
      <circle cx="22" cy="46" r="3" fill="#e5e7eb" />
      <circle cx="46" cy="46" r="3" fill="#e5e7eb" />
      <path d="M18 44h10l6-10h8l4 8" fill="none" stroke="#111827" strokeWidth="2.4" strokeLinecap="round" />
      <rect x="28" y="28" width="14" height="10" rx="2" fill="#facc15" />
      <circle cx="36" cy="20" r="7" fill="#f59e0b" />
      <path d="M30 18h12v4a6 6 0 0 1-12 0z" fill="#111827" />
      <rect x="34" y="12" width="10" height="12" rx="2" fill="#16a34a" />
    </svg>
  );
}

export function OndemandMap({ tr, now }) {
  const progress = trackingProgress(tr, now);
  const rider = useMemo(() => pointAlong(ROUTE, progress), [progress]);
  const d = ROUTE.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]} ${p[1]}`).join(" ");
  const home = ROUTE[ROUTE.length - 1];
  const store = ROUTE[0];
  return (
    <div className="relative h-56 sm:h-64 bg-[#e7e9ee] overflow-hidden" data-testid="ondemand-live-map" aria-label="Store to home delivery map">
      <svg viewBox="0 0 360 210" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        <rect width="360" height="210" fill="#e6e8ed" />
        <g fill="#f4f5f7" stroke="#d5d8df" strokeWidth="1">
          <rect x="8" y="8" width="70" height="48" rx="4" />
          <rect x="86" y="10" width="54" height="40" rx="4" />
          <rect x="148" y="8" width="62" height="36" rx="4" />
          <rect x="220" y="10" width="48" height="42" rx="4" />
          <rect x="278" y="8" width="74" height="40" rx="4" />
          <rect x="10" y="150" width="64" height="50" rx="4" />
          <rect x="84" y="156" width="78" height="44" rx="4" />
          <rect x="172" y="148" width="56" height="52" rx="4" />
          <rect x="238" y="154" width="50" height="46" rx="4" />
          <rect x="298" y="150" width="52" height="50" rx="4" />
          <rect x="18" y="70" width="40" height="28" rx="3" />
          <rect x="168" y="118" width="36" height="22" rx="3" />
          <rect x="300" y="96" width="46" height="28" rx="3" />
        </g>
        <g fill="#ffffff">
          <rect x="0" y="58" width="360" height="16" />
          <rect x="0" y="118" width="360" height="18" />
          <rect x="70" y="0" width="16" height="210" />
          <rect x="188" y="0" width="16" height="210" />
          <rect x="286" y="0" width="14" height="210" />
        </g>
        <path d={d} fill="none" stroke="#1a73e8" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        <g transform={`translate(${store[0]} ${store[1]})`}>
          <circle r="7" fill="#111827" />
          <text y="22" textAnchor="middle" fontSize="11" fontWeight="700" fill="#374151">{tr?.store_label || "Store"}</text>
        </g>
        <g transform={`translate(${home[0]} ${home[1] - 16})`}>
          <path d="M0 18c6-12 12-18 12-18s6 6 12 18c0 0-4 2-12 2s-12-2-12-2z" fill="#fbbc04" stroke="#111827" strokeWidth="1.2" />
          <circle cx="12" cy="12" r="4" fill="#1a73e8" />
        </g>
      </svg>
      <div
        className="absolute z-10"
        data-testid="ondemand-rider-marker"
        style={{
          left: `${(rider.x / 360) * 100}%`,
          top: `${(rider.y / 210) * 100}%`,
          transform: `translate(-50%, -78%) rotate(${rider.ang}deg)`,
          transition: "left 1s linear, top 1s linear",
        }}
      >
        <RiderSprite />
      </div>
    </div>
  );
}

export function OndemandTracking({ slug, orderId, config, order }) {
  const ondemand = isOndemandStorefront(config) || (storefrontThemeId(config) === "classic" && Boolean(config?.zeplo_classic_active));
  const { tr, now } = useOndemandTracking(slug, orderId, ondemand);
  const [help, setHelp] = useState("");
  useEffect(() => {
    if (ondemand && orderId) rememberOndemandOrder(slug, orderId);
  }, [ondemand, slug, orderId]);
  useEffect(() => {
    if (tr && (tr.enabled === false || tr.phase === "delivered")) forgetOndemandOrder(slug);
  }, [tr, slug]);
  if (!ondemand || !tr?.enabled) return null;
  const phone = (config?.store_info?.phone || "").trim();
  const ask = async () => {
    try {
      const { data } = await api.post(`/store/${slug}/orders/${orderId}/help`);
      setHelp(data.message || "Our team will contact you soon regarding your order");
    } catch { setHelp("Could not send help request"); }
  };
  const cod = order?.payment_method === "cod" && tr.phase !== "delivered";
  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm" data-testid="ondemand-tracking">
      <div className="bg-[#0C831F] text-white px-4 py-4">
        <div className="text-sm font-semibold text-white/90" data-testid="ondemand-track-headline">{tr.headline}</div>
        <div className="text-[1.65rem] leading-tight font-extrabold tracking-tight" data-testid="ondemand-track-eta">{tr.subline}</div>
      </div>
      <OndemandMap tr={tr} now={now} />
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center text-xl" aria-hidden="true">🛵</div>
          <div className="min-w-0 flex-1">
            <div className="font-extrabold text-stone-900 leading-snug" data-testid="ondemand-rider-name">{tr.partner_title}</div>
          </div>
          {phone && !tr.help_label ? (
            <a href={`tel:${phone}`} className="w-11 h-11 rounded-full border border-stone-200 flex items-center justify-center text-stone-700" aria-label="Call store" data-testid="ondemand-rider-call">
              <Phone className="w-4 h-4" />
            </a>
          ) : null}
        </div>
        <div className="mt-3 rounded-xl bg-emerald-50 text-emerald-900 text-sm px-3 py-3 leading-relaxed" data-testid="ondemand-track-message">
          {tr.message}
        </div>
        {tr.badge ? <div className="mt-2 text-xs font-semibold text-amber-700">{tr.badge}</div> : null}
        {tr.help_label ? (
          <div className="mt-4 space-y-2">
            <Button type="button" variant="outline" className="rounded-full" data-testid="ondemand-need-help" onClick={ask}>{tr.help_label}</Button>
            {tr.show_store_phone && (tr.store_phone || phone) ? (
              <a href={`tel:${tr.store_phone || phone}`} className="flex items-center gap-2 text-sm font-semibold text-stone-800" data-testid="ondemand-store-phone">
                <Phone className="w-4 h-4" /> Store {tr.store_phone || phone}
              </a>
            ) : null}
            {tr.show_rider_phone && tr.rider_phone ? (
              <a href={`tel:${tr.rider_phone}`} className="flex items-center gap-2 text-sm font-semibold text-stone-800" data-testid="ondemand-rider-phone">
                <Phone className="w-4 h-4" /> Delivery partner {tr.rider_phone}
              </a>
            ) : null}
          </div>
        ) : null}
        {help ? <p className="mt-2 text-sm text-emerald-700" data-testid="ondemand-help-msg">{help}</p> : null}
        {cod ? (
          <div className="mt-4 -mx-4 sm:-mx-5 -mb-4 sm:-mb-5 px-4 py-4 border-t border-stone-100 text-center text-lg font-extrabold text-stone-900" data-testid="ondemand-pay-on-delivery">
            Pay ₹{Number(order.total || 0).toLocaleString("en-IN")} on delivery
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function OndemandLiveTrackFab({ slug, base, config }) {
  const loc = useLocation();
  const ondemand = isOndemandStorefront(config) || (storefrontThemeId(config) === "classic" && Boolean(config?.zeplo_classic_active));
  const [orderId, setOrderId] = useState(() => (ondemand ? readOndemandOrder(slug) : ""));
  const { tr } = useOndemandTracking(slug, orderId, ondemand && Boolean(orderId));
  useEffect(() => {
    if (!ondemand) return undefined;
    const sync = () => setOrderId(readOndemandOrder(slug));
    window.addEventListener("storage", sync);
    window.addEventListener("ondemand-live-order", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("ondemand-live-order", sync);
    };
  }, [ondemand, slug]);
  useEffect(() => {
    if (!tr) return;
    if (tr.enabled === false || tr.phase === "delivered") {
      forgetOndemandOrder(slug);
      setOrderId("");
    }
  }, [tr, slug]);
  if (!ondemand || !orderId || !tr?.enabled || tr.phase === "delivered") return null;
  if (loc.pathname.includes(`/order/${orderId}`)) return null;
  return (
    <Link
      to={`${base}/order/${orderId}`}
      data-testid="ondemand-live-track-fab"
      className="fixed z-[120] right-4 bottom-24 sm:bottom-8 inline-flex items-center gap-2 rounded-full bg-[#0C831F] text-white pl-3 pr-4 py-2.5 shadow-lg shadow-emerald-900/20 text-sm font-bold"
    >
      <span className="inline-flex w-8 h-8 rounded-full bg-white/15 items-center justify-center" aria-hidden="true">🛵</span>
      Order is on the way
    </Link>
  );
}
